import React, { useState } from 'react';
import { ArrowRightIcon, CheckIcon, PhoneIcon } from 'lucide-react';
import { appointmentServices } from '../data/services';
import { practice } from '../data/site';
type Errors = Partial<Record<'name' | 'phone' | 'email' | 'service' | 'date', string>>;
type Props = {
  /** Preselected service, e.g. "Invisalign". */
  defaultService?: string;
  /** Submission source sent with the form, e.g. "Website — Invisalign". */
  source?: string;
  surface?: 'glass' | 'plain';
  className?: string;
};
const fieldClass = 'w-full min-h-[48px] rounded-[10px] border border-paper-line bg-white px-3.5 py-3 text-base text-ink placeholder:text-paper-mute transition-colors focus:border-ink focus:outline-none';
const labelClass = 'mb-1.5 block text-[13px] font-medium tracking-wide text-ink/70';
export function AppointmentForm({
  defaultService = '',
  source = 'Website — General',
  surface = 'glass',
  className = ''
}: Props) {
  const [values, setValues] = useState({
    name: '',
    phone: '',
    email: '',
    patientType: 'new',
    service: defaultService,
    date: '',
    message: ''
  });
  const [errors, setErrors] = useState<Errors>({});
  const [sent, setSent] = useState(false);
  const shell = surface === 'glass' ? 'glass-light rounded-[14px] p-7 sm:p-9' : 'rounded-[14px] border border-ink/10 bg-white p-7 sm:p-9';
  function set(key: keyof typeof values, value: string) {
    setValues(v => ({
      ...v,
      [key]: value
    }));
    setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  }
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next: Errors = {};
    if (values.name.trim().length < 3) next.name = 'Please enter your full name.';
    if (values.phone.replace(/\D/g, '').length < 10) next.phone = 'Please enter a 10-digit phone number.';
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) next.email = 'Please check your email address.';
    if (!values.service) next.service = 'Please select a service of interest.';
    if (!values.date) next.date = 'Please choose a preferred date.';
    setErrors(next);
    if (Object.keys(next).length === 0) setSent(true);
  }
  if (sent) {
    return <div className={`${shell} ${className}`} role="status" aria-live="polite" data-id="element-20" data-mp="davy2276tk3e">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ink text-white" data-id="element-21" data-mp="iba5bi0ysq0d">
          <CheckIcon aria-hidden="true" className="h-5 w-5" strokeWidth={1.5} data-id="element-22" data-mp="nawgllx8fw6j" />
        </span>
        <h3 className="font-display mt-5 text-[24px] leading-[1.2] text-ink" data-id="element-23" data-mp="s8obmot6byey">Request sent</h3>
        <p className="mt-3 text-[15.5px] leading-relaxed text-ink/70" data-id="element-24" data-mp="wzef1wi1hr70">
          Thank you, {values.name.split(' ')[0]}. We received your request, but it is{' '}
          <strong className="font-semibold" data-id="element-25" data-mp="113m825vj5tb1">not a confirmed appointment yet</strong>. Our team
          will contact you at {values.phone} to confirm the day and time.
        </p>
        <a href={practice.phoneHref} className="mt-6 inline-flex min-h-[44px] items-center gap-2 text-[14.5px] font-medium text-ink underline underline-offset-4" data-id="element-26" data-mp="1b07dutfdj8fx">
          <PhoneIcon aria-hidden="true" className="h-4 w-4 text-accent" strokeWidth={1.75} data-id="element-27" data-mp="1g2yctqiy9hnq" />
          Or call us now: {practice.phone}
        </a>
      </div>;
  }
  return <div className={`${shell} ${className}`} data-id="element-28" data-mp="174wgvpe2pf7b">
      <form onSubmit={handleSubmit} noValidate className="space-y-4" data-id="element-29" data-mp="175xpln7m9plv">
        <input type="hidden" name="source" value={source} readOnly data-id="element-30" data-mp="23tlxzdhvm3s8" />

        <div className="grid gap-4 sm:grid-cols-2" data-id="element-31" data-mp="1yur4nhma8wtv">
          <div className="sm:col-span-2" data-id="element-32" data-mp="1yx3usi272di0">
            <label className={labelClass} htmlFor="ap-name" data-id="element-33" data-mp="1u3ivmmov7dh8">
              Full name
            </label>
            <input id="ap-name" type="text" autoComplete="name" placeholder="e.g. Maria Gonzalez" className={fieldClass} value={values.name} onChange={e => set('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'ap-name-error' : undefined} data-id="element-34" data-mp="1q3ihl35oh4ap" />
            <FieldError id="ap-name-error" message={errors.name} data-id="element-35" data-mp="27cjfe0yz7jpg" />
          </div>

          <div data-id="element-36" data-mp="1tc8mkse3vv1n">
            <label className={labelClass} htmlFor="ap-phone" data-id="element-37" data-mp="1tcrtzf64r11f">
              Phone
            </label>
            <input id="ap-phone" type="tel" autoComplete="tel" placeholder="(305) 000-0000" className={fieldClass} value={values.phone} onChange={e => set('phone', e.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'ap-phone-error' : undefined} data-id="element-38" data-mp="1xdamzcjred57" />
            <FieldError id="ap-phone-error" message={errors.phone} data-id="element-39" data-mp="2g9a7y3ebkgfa" />
          </div>

          <div data-id="element-40" data-mp="33zxv1vwt3cmf">
            <label className={labelClass} htmlFor="ap-email" data-id="element-41" data-mp="33ze9m57y663n">
              Email
            </label>
            <input id="ap-email" type="email" autoComplete="email" placeholder="name@email.com" className={fieldClass} value={values.email} onChange={e => set('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'ap-email-error' : undefined} data-id="element-42" data-mp="38tsdkw0odljx" />
            <FieldError id="ap-email-error" message={errors.email} data-id="element-43" data-mp="edkwllk1vpr6" />
          </div>
        </div>

        <fieldset data-id="element-44" data-mp="9fjbkv32dgd2">
          <legend className={labelClass} data-id="element-45" data-mp="9g2v2g8d4ct7">Patient type</legend>
          <div className="grid grid-cols-2 gap-3" data-id="element-46" data-mp="ocac57oh4y49">
            {[{
            value: 'new',
            label: 'New patient'
          }, {
            value: 'existing',
            label: 'Existing patient'
          }].map(option => {
            const active = values.patientType === option.value;
            return <label key={option.value} className={`flex min-h-[48px] cursor-pointer items-center justify-center rounded-[10px] border px-3 text-center text-sm font-medium transition-colors ${active ? 'border-ink bg-ink text-white' : 'border-paper-line bg-white text-ink/70 hover:border-ink/40'}`} data-id="element-47" data-mp="1usmdtiqi8y4l">
                  <input type="radio" name="patientType" value={option.value} checked={active} onChange={e => set('patientType', e.target.value)} className="sr-only" data-id="element-48" data-mp="2griafjglyn74" />
                  {option.label}
                </label>;
          })}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2" data-id="element-49" data-mp="1xw22pw0r6k4q">
          <div data-id="element-50" data-mp="10fczbxw5n9t8">
            <label className={labelClass} htmlFor="ap-service" data-id="element-51" data-mp="10ds8ai91u904">
              Service of interest
            </label>
            <select id="ap-service" className={fieldClass} value={values.service} onChange={e => set('service', e.target.value)} aria-invalid={Boolean(errors.service)} aria-describedby={errors.service ? 'ap-service-error' : undefined} data-id="element-52" data-mp="15btsj6i7oed6">
              <option value="" data-id="element-53" data-mp="34oeyrt5rcuj8">Select a service</option>
              {appointmentServices.map(s => <option key={s} value={s} data-id="element-54" data-mp="2zplkvzmk71ij">
                  {s}
                </option>)}
            </select>
            <FieldError id="ap-service-error" message={errors.service} data-id="element-55" data-mp="2uxajesc677nv" />
          </div>

          <div data-id="element-56" data-mp="2qvyuyq518sh9">
            <label className={labelClass} htmlFor="ap-date" data-id="element-57" data-mp="2qxjp4enfq9el">
              Preferred date
            </label>
            <input id="ap-date" type="date" className={fieldClass} value={values.date} onChange={e => set('date', e.target.value)} aria-invalid={Boolean(errors.date)} aria-describedby={errors.date ? 'ap-date-error' : undefined} data-id="element-58" data-mp="2lz8e40lelsus" />
            <FieldError id="ap-date-error" message={errors.date} data-id="element-59" data-mp="8pw461q532ne" />
          </div>
        </div>

        <div data-id="element-60" data-mp="3qvhev9onr58e">
          <label className={labelClass} htmlFor="ap-message" data-id="element-61" data-mp="3qw0lhu8uy774">
            Message <span className="font-normal text-ink/40" data-id="element-62" data-mp="3qxv77arye3c9">(optional)</span>
          </label>
          <textarea id="ap-message" rows={3} placeholder="Tell us briefly what you need." className={`${fieldClass} resize-none`} value={values.message} onChange={e => set('message', e.target.value)} data-id="element-63" data-mp="3vwoo7d6g8ch6" />
        </div>

        <button type="submit" className="group flex min-h-[54px] w-full items-center justify-between rounded-[10px] bg-ink px-5 text-[15px] font-medium text-white transition-colors hover:bg-ink-char" data-id="element-64" data-mp="382wr26ntfqbg">
          Request an appointment
          <span className="hover-arrow" data-id="element-65" data-mp="334v9xr9ya12m">
            <ArrowRightIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.75} data-id="element-66" data-mp="337r6bb65ghoy" />
          </span>
        </button>

        <p className="text-[13px] leading-relaxed text-ink/50" data-id="element-67" data-mp="2fiszstfu8t9x">
          Submitting this form is a request, not a confirmed appointment. Our team will contact
          you to confirm it. To book instantly, visit{' '}
          <a href={practice.bookingUrl} target="_blank" rel="noreferrer" className="font-medium text-ink underline underline-offset-2" data-id="element-68" data-mp="2oiy4gvtnddke">
            lima.dental
          </a>
          . If you need urgent care, please call{' '}
          <a href={practice.phoneHref} className="font-medium text-ink underline underline-offset-2" data-id="element-69" data-mp="3c9bp419wka4y">
            {practice.phone}
          </a>
          .
        </p>
      </form>
    </div>;
}
function FieldError({
  id,
  message
}: {
  id: string;
  message?: string;
}) {
  if (!message) return null;
  return <p id={id} className="mt-1.5 text-[13px] font-medium text-accent" data-id="element-70" data-mp="3h9g8bz273iyv">
      {message}
    </p>;
}